import React from 'react';
// Imports only from core @react-pdf/renderer
import { Document, Page, Text, View, StyleSheet } from '@react-pdf/renderer';

// --- Configuration Constants ---

const MONTHLY_COLUMNS = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];
const FIXED_COLUMNS = ['DEPTNO', 'ACCOUNT', 'CUSTOMER', 'TYPE', 'YEAR']; // Using keys directly for simplicity
const TOTAL_COLUMNS = FIXED_COLUMNS.length + MONTHLY_COLUMNS.length + 1; // 18 columns

// --- Helper Functions ---

const formatNumber = (val) => 
    Number(val || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// --- Column Widths Calculation ---

// Total columns = 18. Using Landscape A4 for 18 columns.
const descriptiveWidth = 6; // Width for each of the 5 fixed descriptive columns
const monthWidth = 4.5;    // Width for each of the 12 month columns
const totalWidth = 6;      // Width for the final TOTAL column
const totalCalculatedWidth = (FIXED_COLUMNS.length * descriptiveWidth) + (MONTHLY_COLUMNS.length * monthWidth) + totalWidth; 
// 5*6 + 12*4.5 + 6 = 30 + 54 + 6 = 90% (Leaving 10% for padding/error margin)

const getColumnWidth = (index, totalColumns) => {
    if (index < FIXED_COLUMNS.length) {
        return descriptiveWidth;
    } else if (index < totalColumns - 1) { // Month columns
        return monthWidth;
    } else { // TOTAL column
        return totalWidth;
    }
}

// --- Stylesheet for PDF Component ---

const styles = StyleSheet.create({
    page: {
        flexDirection: 'column',
        backgroundColor: '#ffffff',
        padding: 20,
        fontFamily: 'Helvetica', 
        fontSize: 8, // Using small font to fit 18 columns
    },
    title: {
        fontSize: 14,
        marginBottom: 10,
        textAlign: 'center',
        fontWeight: 'bold',
    },
    table: {
        display: 'flex',
        width: 'auto',
        borderStyle: 'solid',
        borderWidth: 1,
        borderRightWidth: 0,
        borderBottomWidth: 0,
    },
    tableRow: {
        margin: 'auto',
        flexDirection: 'row',
        height: 16, // Reduced height
        borderBottomColor: '#ccc',
        borderBottomWidth: 0.5,
    },
    tableHeader: {
        backgroundColor: '#e9e9e9', 
        borderBottomWidth: 1.5,
        borderBottomColor: '#000',
        fontWeight: 'bold',
        height: 22, // Reduced height
    },
    columnHeader: {
        borderStyle: 'solid',
        borderWidth: 1,
        borderLeftWidth: 0,
        borderTopWidth: 0,
        padding: 3,
        textAlign: 'center',
        fontWeight: 'bold',
    },
    detailCell: {
        borderStyle: 'solid',
        borderWidth: 1,
        borderLeftWidth: 0,
        borderTopWidth: 0,
        padding: 2,
        overflow: 'hidden',
    },
    numericCell: {
        textAlign: 'right',
    },
    grandTotalRow: {
        backgroundColor: '#b3b3b3',
        borderTopWidth: 2,
        borderTopColor: '#000',
        height: 22,
        fontWeight: 'bold',
        flexDirection: 'row',
    },
    grandTotalCell: {
        padding: 4,
        borderStyle: 'solid',
        borderWidth: 1,
        borderLeftWidth: 0,
        borderTopWidth: 0,
        fontSize: 9,
        fontWeight: 'bold',
    },
    emptyData: {
        padding: 10,
        textAlign: 'center',
        color: 'gray',
    }
});


// --- The PDF Component ---

export const SalesTableMonthlyPDF = ({ rows, grandColumnTotals, filters }) => {
    
    // Safety check
    if (!rows || rows.length === 0) {
        return (
            <Document title="Monthly Sales Analysis Report">
                <Page size="A4" style={styles.page} orientation="landscape">
                    <Text style={styles.emptyData}>No monthly sales data found for the selected filters.</Text>
                </Page>
            </Document>
        );
    }
    
    const headerKeys = [...FIXED_COLUMNS, ...MONTHLY_COLUMNS, 'TOTAL'];
    const totalKeys = [...MONTHLY_COLUMNS, 'TOTAL'];
    
    // --- Renderer Functions ---

    const renderHeader = () => (
        <View style={[styles.tableRow, styles.tableHeader]} fixed>
            {headerKeys.map((key, index) => (
                <Text 
                    key={key} 
                    style={[
                        styles.columnHeader, 
                        { width: `${getColumnWidth(index, headerKeys.length)}%` },
                        index >= FIXED_COLUMNS.length ? styles.numericCell : {} // Right align month and total columns
                    ]}
                >
                    {key}
                </Text>
            ))}
        </View>
    );

    const renderRow = (row, index) => (
        <View key={`month-row-${index}`} style={styles.tableRow} wrap={false}>
            {headerKeys.map((key, index) => {
                const content = row[key];
                const isNumeric = index >= FIXED_COLUMNS.length;
                const isTotal = key === 'TOTAL';

                return (
                    <Text 
                        key={key} 
                        style={[
                            styles.detailCell, 
                            { width: `${getColumnWidth(index, headerKeys.length)}%` }, 
                            isNumeric ? styles.numericCell : {},
                            isTotal ? {fontWeight: 'bold'} : {}
                        ]}
                    >
                        {isNumeric ? formatNumber(content) : content}
                    </Text>
                );
            })}
        </View>
    );

    const renderFooter = () => {
        if (!grandColumnTotals || Object.keys(grandColumnTotals).length === 0) return null;

        // Calculate the combined width for the "GRAND TOTAL:" label cell
        const descriptiveSpanWidth = FIXED_COLUMNS.length * descriptiveWidth;

        return (
            <View style={[styles.grandTotalRow]} fixed>
                {/* Grand Total Label Cell (Spanning FIXED_COLUMNS) */}
                <Text style={[styles.grandTotalCell, { width: `${descriptiveSpanWidth}%`, textAlign: 'right' }]}>
                    GRAND TOTAL:
                </Text>

                {/* Grand Total Value Cells */}
                {totalKeys.map((key, index) => (
                    <Text 
                        key={key} 
                        style={[
                            styles.grandTotalCell, 
                            styles.numericCell, 
                            { width: `${key === 'TOTAL' ? totalWidth : monthWidth}%` }
                        ]}
                    >
                        {formatNumber(grandColumnTotals[key])}
                    </Text>
                ))}
            </View>
        );
    }


    return (
        <Document title="Monthly Sales Analysis Report">
            <Page size="A4" orientation="landscape" style={styles.page} wrap>
                <Text style={styles.title}>Monthly Sales Analysis Report</Text>
                
                {/* Optional: Include Filter Summary */}
                {filters && (
                    <View style={{ marginBottom: 10 }}>
                        <Text style={{ fontSize: 7 }}>
                            Filters: Date Range ({filters?.startDate || 'N/A'} to {filters?.endDate || 'N/A'})
                        </Text>
                    </View>
                )}

                <View style={styles.table}>
                    {renderHeader()}
                    <View>
                        {rows.map(renderRow)}
                    </View>
                </View>

                {/* Footer is rendered outside the main table View for better fixed positioning */}
                {renderFooter()}

                {/* Page Numbering */}
                <Text style={{ position: 'absolute', bottom: 10, right: 20, fontSize: 7 }} fixed>
                    Page <Text render={({ pageNumber, totalPages }) => (`${pageNumber} of ${totalPages}`)} />
                </Text>
            </Page>
        </Document>
    );
};

export default SalesTableMonthlyPDF;